"use client";

import { useAtom } from "jotai";
import { sampleSize, groupBy, type Dictionary } from "lodash";
import { selectedTagIdsAtom, suggestedTagsAtom } from "@/store/sites";
import type { Tag } from "@/types";

function useTags(tags: Tag[]) {
  const _tags = tags;

  const [suggestedTags, setSuggestedTags] = useAtom(suggestedTagsAtom);
  const [selectedTagIds, setSelectedTagIds] = useAtom(selectedTagIdsAtom);

  const getTagByIds = (ids: string[], tags?: Tag[]): Tag[] | undefined => {
    const data = tags ?? _tags;
    return data.filter((tag) => ids.includes(String(tag.id)));
  };

  const getFavouriteTags = (tags?: Tag[]): Tag[] => {
    const data = tags ?? _tags;
    return data.filter((tag) => tag.isFavourite);
  };

  const sampleTags = (n = 8, tags?: Tag[]): Tag[] => {
    const data = tags ?? _tags;
    return sampleSize(data, n);
  };

  const addSelectedTag = (newTag: Tag) => {
    const getSelectedTagIds = () => {
      if (selectedTagIds.includes(newTag.id)) {
        return selectedTagIds;
      }
      return [...selectedTagIds, newTag.id];
    };

    const newSelectedTagIds = getSelectedTagIds();
    setSelectedTagIds(newSelectedTagIds);
  };

  const retrieveTagIds = (tags?: Tag[]): number[] => {
    const data = tags ?? _tags;
    return data.map((tag) => tag.id);
  };

  const sortTagsAlphabetically = (locale: string, tags?: Tag[]): Tag[] => {
    const data = tags ?? _tags;
    return data.sort((a, b) => {
      return a.name[locale]
        .toLowerCase()
        .localeCompare(b.name[locale].toLowerCase(), locale, {
          sensitivity: "base",
        });
    });
  };

  const groupTagsAlphabetically = (
    locale: string,
    tags?: Tag[],
  ): Dictionary<Tag[]> => {
    const data = tags ?? _tags;
    const sortedTags = sortTagsAlphabetically(locale, data);
    return groupBy(sortedTags, (tag) => {
      return tag.name[locale][0]
        .toLowerCase()
        .normalize("NFD")
        .replaceAll(/\p{Diacritic}/gu, "");
    });
  };

  const favouriteTags: Tag[] = getFavouriteTags();

  return {
    favouriteTags,
    getFavouriteTags,
    sampleTags,
    addSelectedTag,
    selectedTagIds,
    setSelectedTagIds,
    suggestedTags,
    setSuggestedTags,
    getTagByIds,
    groupTagsAlphabetically,
    sortTagsAlphabetically,
    retrieveTagIds,
  };
}

export { useTags };
